<template>
  <ModalShell :visible="configSet.migrationDialogVisible.value" :z-index="2000" :close-on-overlay="false">
    <ModalPanel width="420px" max-height="80vh" background="var(--bg-card)">
      <div class="migration-header">
        <span class="migration-title">{{ t('migration_dialog_title') }}</span>
        <span class="migration-desc">{{ t('migration_dialog_desc', { total: candidates.length }) }}</span>
      </div>

      <div class="migration-list scrollbar_thumb">
        <div
          v-for="item in candidates"
          :key="item.index"
          class="migration-item"
          :class="{
            selected: selectedIndices.has(item.index),
            disabled: !selectedIndices.has(item.index) && selectedIndices.size >= 4,
            locked: item.isCurrent,
          }"
          @click="toggleSelect(item)"
        >
          <div class="migration-check">
            <div class="check-box" :class="{ checked: selectedIndices.has(item.index) }">
              <svg v-if="selectedIndices.has(item.index)" viewBox="0 0 16 16" fill="none">
                <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
          </div>
          <div class="migration-item-info">
            <span class="migration-item-name">{{ item.name }}</span>
            <span v-if="item.isCurrent" class="migration-item-badge">{{ t('migration_current_config') }}</span>
          </div>
        </div>
      </div>

      <div class="migration-footer">
        <span class="migration-hint">
          {{ t('migration_selected_count', { count: selectedIndices.size }) }}
          <template v-if="candidates.length - selectedIndices.size > 0"> · {{ t('migration_will_archive', { count: candidates.length - selectedIndices.size }) }} </template>
        </span>
        <button class="mgx_action_btn primary" :disabled="selectedIndices.size !== 4" @click="confirm">
          {{ t('migration_confirm') }}
        </button>
      </div>
    </ModalPanel>
  </ModalShell>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { configSet } from '@/service/driver/config_set'
import i18n from '@/languages/i18n'
import ModalShell from '../common/ModalShell.vue'
import ModalPanel from '../common/ModalPanel.vue'
const { t } = i18n.global

const selectedIndices = ref(new Set<number>())

const candidates = computed(() => configSet.migrationCandidates.value)

// 当弹窗打开时，自动选中前 4 个（当前配置始终选中）
watch(
  () => configSet.migrationDialogVisible.value,
  (visible) => {
    if (!visible) return
    const s = new Set<number>()
    // 先加入当前配置
    const current = candidates.value.find((c) => c.isCurrent)
    if (current) s.add(current.index)
    // 再按顺序填满到 4 个
    for (const c of candidates.value) {
      if (s.size >= 4) break
      s.add(c.index)
    }
    selectedIndices.value = s
  },
)

const toggleSelect = (item: { index: number; isCurrent: boolean }) => {
  // 当前配置不可取消
  if (item.isCurrent) return

  const s = new Set(selectedIndices.value)
  if (s.has(item.index)) {
    s.delete(item.index)
  } else if (s.size < 4) {
    s.add(item.index)
  }
  selectedIndices.value = s
}

const confirm = async () => {
  if (selectedIndices.value.size !== 4) return
  // 按候选列表顺序排列，确保槽位分配顺序
  const keepIndices = candidates.value.filter((c) => selectedIndices.value.has(c.index)).map((c) => c.index)
  await configSet.completeMigration(keepIndices)
}
</script>

<style scoped lang="less">
.migration-header {
  padding: 24px 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.migration-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-secondary);
}

.migration-desc {
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.5;
}

.migration-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 16px;
  max-height: 360px;
}

.migration-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: var(--radius-m);
  cursor: pointer;
  transition: background 0.15s ease;
  user-select: none;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  &.selected {
    background: rgba(var(--brand-color-rgb), 0.08);
  }

  &.disabled {
    opacity: 0.4;
    cursor: not-allowed;
    &:hover {
      background: transparent;
    }
  }

  &.locked {
    cursor: default;
  }
}

.migration-check {
  flex-shrink: 0;
}

.check-box {
  width: 20px;
  height: 20px;
  border-radius: 5px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &.checked {
    background: var(--brand-color-pure);
    border-color: var(--brand-color-pure);
    color: #fff;
  }

  svg {
    width: 14px;
    height: 14px;
  }
}

.migration-item-info {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.migration-item-name {
  font-size: 14px;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.migration-item-badge {
  flex-shrink: 0;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  background: rgba(var(--brand-color-rgb), 0.12);
  color: var(--brand-color-pure);
}

.migration-footer {
  padding: 16px 24px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-top: var(--panel-border-soft);

  .mgx_action_btn {
    flex-shrink: 0;
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
}

.migration-hint {
  font-size: 12px;
  color: var(--text-secondary);
}
</style>
